/* Small DOM helpers: the modal used for reports, numeric prompts and root selection, plus
 * the status line.  Kept deliberately thin — the app's substance is in core/. */

const modal = document.getElementById('modal') as HTMLDialogElement;
const modalTitle = document.getElementById('modal-title') as HTMLElement;
const modalBody = document.getElementById('modal-body') as HTMLElement;
const modalActions = document.getElementById('modal-actions') as HTMLElement;
const toastEl = document.getElementById('toast') as HTMLElement;
const statsEl = document.getElementById('stats') as HTMLElement;

let toastTimer = 0;

/** Transient message in the status bar. */
export function toast(msg: string, ms = 6000): void {
  toastEl.textContent = msg ? `${msg}   |   ` : '';
  clearTimeout(toastTimer);
  if (msg) toastTimer = window.setTimeout(() => { toastEl.textContent = ''; }, ms);
}

export function stats(msg: string): void {
  statsEl.textContent = msg;
}

const claimsEl = document.getElementById('claims') as HTMLElement;

/** The claims about solids' line in the footer, and how it reads: going, failed (one refuted, or
 *  the judging failed) or finished. */
export function judgingLine(msg: string, state: 'going' | 'failed' | 'finished' = 'going'): void {
  claimsEl.textContent = msg;
  claimsEl.className = state === 'going' ? '' : state;
}

const scenariosEl = document.getElementById('scenarios') as HTMLElement;

/** The load scenarios' line in the footer: failed (a requirement that does not hold, or has no
 *  answer) or finished, with every sentence the core said of them as its tooltip; empty, none. */
export function scenarioLine(msg: string, state: '' | 'failed' | 'finished' = '', said = ''): void {
  scenariosEl.textContent = msg;
  scenariosEl.className = state;
  scenariosEl.title = said;
}

function open<T>(title: string, build: (resolve: (v: T) => void) => void): Promise<T> {
  modalTitle.textContent = title;
  modalBody.replaceChildren();
  modalActions.replaceChildren();
  return new Promise<T>((resolve) => {
    let done = false;
    const finish = (v: T): void => {
      if (done) return;
      done = true;
      modal.close();
      resolve(v);
    };
    modal.addEventListener('close', () => finish(undefined as T), { once: true });
    build(finish);
    modal.showModal();
  });
}

export function button(label: string, onClick: () => void, primary = false): HTMLButtonElement {
  const b = document.createElement('button');
  b.textContent = label;
  if (primary) b.style.borderColor = 'var(--accent)';
  b.addEventListener('click', onClick);
  return b;
}

/** A modal whose only action is Close: the caller fills the body.  Nothing in one is
 *  confirmed — a report has nothing to confirm, and a sheet of switches has already taken
 *  effect by the time you read them. */
export function showSheet(title: string, build: (body: HTMLElement) => void): Promise<void> {
  return open<void>(title, (resolve) => {
    build(modalBody);
    modalActions.append(button('Close', () => resolve(), true));
  });
}

/** Scrollable read-only report. */
export function showReport(title: string, text: string): Promise<void> {
  return showSheet(title, (body) => {
    const pre = document.createElement('pre');
    pre.textContent = text;
    body.append(pre);
  });
}

/** Numeric prompt; resolves null on cancel. */
export function askNumber(title: string, label: string, value: number, step = 'any'): Promise<number | null> {
  return open<number | null>(title, (resolve) => {
    const l = document.createElement('label');
    l.textContent = label;
    l.style.display = 'block';
    l.style.marginBottom = '6px';
    const input = document.createElement('input');
    input.type = 'number';
    input.step = step;
    input.value = String(Number(value.toPrecision(10)));
    const submit = (): void => {
      const v = Number(input.value);
      resolve(Number.isFinite(v) ? v : null);
    };
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } });
    modalBody.append(l, input);
    modalActions.append(button('Cancel', () => resolve(null)), button('OK', submit, true));
    setTimeout(() => { input.focus(); input.select(); }, 0);
  });
}

/** A form: the caller fills the body with fields (`addText`, `addNumber`, `addSelect`) whose
 *  callbacks hold the answers, and this resolves true on OK and false on Cancel.  Enter in any
 *  field is OK.  Nothing is validated here — what a field's text means is the caller's, and
 *  usually the core's, to say. */
export function askFields(title: string, build: (body: HTMLElement) => void): Promise<boolean> {
  return open<boolean>(title, (resolve) => {
    const box = document.createElement('div');
    box.className = 'fields';
    build(box);
    box.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
        e.preventDefault();
        resolve(true);
      }
    });
    modalBody.append(box);
    modalActions.append(button('Cancel', () => resolve(false)),
                        button('OK', () => resolve(true), true));
    setTimeout(() => box.querySelector('input')?.focus(), 0);
  });
}

/** Pick one of a list; resolves null on cancel. */
export function askChoice(title: string, label: string,
                          options: (string | { title: string; description: string })[],
                          searchable = false): Promise<number | null> {
  return open<number | null>(title, (resolve) => {
    const p = document.createElement('p');
    p.textContent = label;
    const box = document.createElement('div');
    box.className = 'choices';
    const rows = options.map((o, i) => {
      const row = button(typeof o === 'string' ? o : '', () => resolve(i));
      if (typeof o !== 'string') {
        row.className = 'described-choice';
        const title = document.createElement('strong');
        title.textContent = o.title;
        const description = document.createElement('span');
        description.textContent = o.description;
        row.append(title, description);
      }
      box.append(row);
      return row;
    });
    modalBody.append(p);
    if (searchable) {
      const search = document.createElement('input');
      search.type = 'search';
      search.placeholder = 'Search examples';
      search.setAttribute('aria-label', 'Search examples');
      const empty = document.createElement('p');
      empty.textContent = 'No examples match your search.';
      empty.hidden = true;
      search.addEventListener('input', () => {
        const query = search.value.trim().toLocaleLowerCase();
        for (const row of rows) row.hidden = !row.textContent?.toLocaleLowerCase().includes(query);
        empty.hidden = rows.some((r) => !r.hidden);
      });
      // the bar stays put above the list as it scrolls, so the search is in reach however far down
      const bar = document.createElement('div');
      bar.className = 'search-bar';
      bar.append(search);
      modalBody.append(bar, empty);
    }
    modalBody.append(box);
    modalActions.append(button('Cancel', () => resolve(null)));
  });
}

export interface ToolbarButton {
  label: string;
  onClick: () => void;
  key?: string;
  toggle?: boolean;
  title?: string;
}

export function addButton(bar: HTMLElement, spec: ToolbarButton): HTMLButtonElement {
  const b = document.createElement('button');
  b.textContent = spec.label;
  if (spec.key) {
    const k = document.createElement('kbd');
    k.textContent = spec.key.toUpperCase();
    b.append(k);
  }
  if (spec.title) b.title = spec.title;
  if (spec.toggle) b.setAttribute('aria-pressed', 'false');
  b.addEventListener('click', spec.onClick);
  bar.append(b);
  return b;
}

/** An outbound link on a bar.  Its own tab, because following it leaves the sketch behind. */
export function addLink(bar: HTMLElement, label: string, href: string): HTMLAnchorElement {
  const a = document.createElement('a');
  a.textContent = label;
  a.href = href;
  a.target = '_blank';
  a.rel = 'noopener';
  bar.append(a);
  return a;
}

export function addSeparator(bar: HTMLElement): void {
  const s = document.createElement('span');
  s.className = 'sep';
  bar.append(s);
}

/** The wrapper a labelled control sits in, so the text is part of the control's hit area and
 *  the tooltip covers both.  Callers append in the order the control reads: a checkbox before
 *  its label, a select after it. */
function field(bar: HTMLElement, title?: string): HTMLLabelElement {
  const l = document.createElement('label');
  l.className = 'check';
  if (title) l.title = title;
  bar.append(l);
  return l;
}

export function addCheckbox(bar: HTMLElement, label: string, checked: boolean,
                            onChange: (v: boolean) => void, title?: string): HTMLInputElement {
  const c = document.createElement('input');
  c.type = 'checkbox';
  c.checked = checked;
  c.addEventListener('change', () => onChange(c.checked));
  field(bar, title).append(c, document.createTextNode(label));
  return c;
}

/** A text field.  `onChange` hears every keystroke, so the caller's variable is the field's
 *  current text by the time the form is accepted. */
export function addText(bar: HTMLElement, label: string, value: string,
                        onChange: (v: string) => void, title?: string): HTMLInputElement {
  const t = document.createElement('input');
  t.type = 'text';
  t.value = value;
  t.addEventListener('input', () => onChange(t.value));
  field(bar, title).append(document.createTextNode(label), t);
  return t;
}

/** A number field, handed on as the *text* typed: a form's numbers are spliced into the
 *  source as written, and `-90` and `-90.0` are two spellings the document keeps apart. */
export function addNumber(bar: HTMLElement, label: string, value: number,
                          onChange: (v: string) => void, title?: string): HTMLInputElement {
  const t = document.createElement('input');
  t.type = 'number';
  t.step = 'any';
  t.value = String(value);
  t.addEventListener('input', () => onChange(t.value));
  field(bar, title).append(document.createTextNode(label), t);
  return t;
}

export function addSelect(bar: HTMLElement, label: string, options: string[], value: string,
                          onChange: (v: string) => void, title?: string): HTMLSelectElement {
  const s = document.createElement('select');
  for (const o of options) {
    const opt = document.createElement('option');
    opt.value = o;
    opt.textContent = o;
    s.append(opt);
  }
  s.value = value;
  s.addEventListener('change', () => onChange(s.value));
  field(bar, title).append(document.createTextNode(label), s);
  return s;
}

/* -- topics ----------------------------------------------------------------------- */

/** A topic on a bar: buttons standing side by side while the bar has room for them, and folded
 *  under one button named for the topic when it has not (`fitBar`).  The buttons are the same
 *  elements either way, so whatever holds one — a tool's pressed state — keeps holding it.
 *  Folded, the topic drops its buttons as a menu does, and shares the menu bar's one open menu.
 *  `null` in the table is a divider.  Returns the buttons, in the order of the table. */
export function addTopic(bar: HTMLElement, label: string,
                         specs: (ToolbarButton | null)[]): HTMLButtonElement[] {
  const wrap = document.createElement('div');
  wrap.className = 'topic';
  const top = document.createElement('button');
  top.className = 'topic-head';
  top.textContent = label;
  top.setAttribute('aria-expanded', 'false');
  top.setAttribute('aria-haspopup', 'true');
  const items = document.createElement('div');
  items.className = 'topic-items';
  // dropped, the list hangs from the head's left edge unless that runs it off the screen, and
  // reaches no lower than the screen does: past that it scrolls
  dropsOn(top, () => {
    wrap.classList.remove('flip');
    const at = items.getBoundingClientRect();
    if (at.right > document.documentElement.clientWidth) wrap.classList.add('flip');
    items.style.maxHeight = `${Math.max(120, window.innerHeight - at.top - 8)}px`;
  });
  // a choice made from the dropped list is the list done with
  items.addEventListener('click', (e) => {
    if (wrap.classList.contains('folded') && (e.target as HTMLElement).closest('button')) {
      closeMenus();
    }
  });
  wrap.append(top, items);
  bar.append(wrap);
  return specs.flatMap((spec) => {
    if (spec) return [addButton(items, spec)];
    addSeparator(items);
    return [];
  });
}

/** Fold a bar's topics, last first, until its buttons fit on one row — and where folding every
 *  topic is not enough, let the row wrap.  The buttons keep their size: a bar short of room
 *  says less, never smaller. */
function fitBar(bar: HTMLElement): void {
  const topics = [...bar.children].filter((c): c is HTMLElement => c.classList.contains('topic'));
  const before = topics.map((t) => t.classList.contains('folded'));
  bar.classList.remove('wrapping');
  for (const t of topics) t.classList.remove('folded');
  const over = (): boolean => bar.scrollWidth > bar.clientWidth;
  for (let i = topics.length - 1; i >= 0 && over(); i -= 1) topics[i].classList.add('folded');
  if (over()) bar.classList.add('wrapping');
  // a list dropped from a head that is now standing open, or folded away, has nothing under it
  if (topics.some((t, i) => t.classList.contains('folded') !== before[i])) closeMenus();
}

/** Keep each bar fitted to the room it has, and where every bar has folded down to its heads,
 *  stand them side by side on one row (`compact` on the host).  Each bar is fitted standing on
 *  its own row, the width it will have when it unfolds; only the host's width decides, so the
 *  row the bars share, and the height a fold or a wrap changes, ask nothing again. */
export function fitBars(host: HTMLElement, bars: HTMLElement[]): void {
  const heads = (b: HTMLElement): boolean => [...b.children]
    .every((c) => c.classList.contains('topic') && c.classList.contains('folded'));
  let width = -1;
  const fit = (): void => {
    if (host.clientWidth === width) return;
    width = host.clientWidth;
    host.classList.remove('compact');
    for (const b of bars) fitBar(b);
    host.classList.toggle('compact', bars.every(heads));
  };
  for (const b of bars) b.classList.add('fitted');
  new ResizeObserver(fit).observe(host);
  fit();
}

/* -- menu bar ------------------------------------------------------------------- */

/** A menu item is a button that happens to live in a list, so it is described the same way —
 *  `toggle` is the one thing a menu has no use for. */
export type MenuItem = Omit<ToolbarButton, 'toggle'> & {
  /** A check box's: whether it is ticked, asked each time the menu drops. */
  checked?: () => boolean;
};

/** The one menu that is down, if any: its top button, whose `aria-expanded` is what the CSS
 *  drops the list on.  One variable, so nothing can disagree about what is open. */
let openMenu: HTMLButtonElement | null = null;

/** Shut the open menu; true if there was one, which is how Escape knows whether it has
 *  already done something before it goes on to back out of the tool. */
export function closeMenus(): boolean {
  if (!openMenu) return false;
  openMenu.setAttribute('aria-expanded', 'false');
  openMenu = null;
  return true;
}

/** Make a button the head of a list that drops from it: the menu bar's menus and a folded
 *  topic's.  The one open list is `openMenu`, whichever kind it is. */
function dropsOn(top: HTMLButtonElement, dropped?: () => void): void {
  const drop = (): void => {
    closeMenus();
    top.setAttribute('aria-expanded', 'true');
    openMenu = top;
    dropped?.();
  };
  // pointerdown rather than click, so the menu is up under the finger that opened it, and
  // default-prevented so the focus stays wherever it was and the key handler keeps working
  top.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    if (openMenu === top) closeMenus();
    else drop();
  });
  top.addEventListener('pointerenter', (e) => {
    // a finger has no hover: its press is what reaches pointerdown, which decides
    if (e.pointerType !== 'touch' && openMenu && openMenu !== top) drop();
  });
}

/** One menu on the bar: a name, and the items under it with `null` for a dividing rule and a
 *  string for a heading over the items after it (View ▸ Show).  A check box is ticked as it stands
 *  each time the menu drops.
 *  Once one menu is open the others take over on hover, the way a desktop menu bar behaves. */
export function addMenu(bar: HTMLElement, label: string,
                        items: (MenuItem | string | null)[]): void {
  const wrap = document.createElement('div');
  wrap.className = 'menu';
  const top = document.createElement('button');
  top.textContent = label;
  top.setAttribute('aria-expanded', 'false');
  // a check box is ticked as it stands each time the menu drops
  const checks: [HTMLButtonElement, () => boolean][] = [];
  dropsOn(top, () => {
    for (const [b, on] of checks) b.setAttribute('aria-checked', String(on()));
  });
  const list = document.createElement('ul');
  for (const item of items) {
    const li = document.createElement('li');
    if (!item) {
      li.className = 'sep';
    } else if (typeof item === 'string') {
      // a heading over the items after it: View ▸ Show
      li.className = 'head';
      li.textContent = item;
    } else {
      const b = addButton(li, { ...item, onClick: () => { closeMenus(); item.onClick(); } });
      if (item.checked) {
        b.setAttribute('role', 'menuitemcheckbox');
        checks.push([b, item.checked]);
      }
    }
    list.append(li);
  }
  wrap.append(top, list);
  bar.append(wrap);
}

/* A press anywhere else puts the menu away — including on the canvas, where the same press
 * goes on to do whatever it was going to do. */
document.addEventListener('pointerdown', (e) => {
  if (openMenu && !(e.target as HTMLElement).closest('.menu, .topic')) closeMenus();
});

/** What a downloaded file is, by what it is called.  The name already states the format, so a
 *  caller that had to state it again would be writing one fact twice — and a Blob given the
 *  wrong type fails silently, which is the way to get it wrong that nobody notices.  A format
 *  the table does not know is offered as plain text: everything this app writes is text. */
const MIME: Record<string, string> = {
  json: 'application/json',
  svg: 'image/svg+xml',
  sv: 'text/plain',
  glb: 'model/gltf-binary',
  stl: 'model/stl',
};

/** Download a string as a file — the document as JSON, the source as `.sv`, the drawing
 *  as SVG. */
export function download(name: string, data: string | Uint8Array): void {
  // bytes as readily as text: a `.glb` and a `.stl` are binary, and a Blob takes either
  const part: BlobPart = typeof data === 'string' ? data : new Uint8Array(data);
  const blob = new Blob([part], { type: MIME[name.split('.').pop() ?? ''] ?? 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Ask the browser for a local file (null if the user cancels).  The dialog is the same
 *  whatever is wanted off the file afterwards, so it is asked for in one place — what a caller
 *  does with the `File` is the only part that differs. */
function pickFile(accept: string): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.addEventListener('change', () => resolve(input.files?.[0] ?? null));
    input.addEventListener('cancel', () => resolve(null));
    input.click();
  });
}

/** Ask for a local file and return its text (null if the user cancels). */
export function openFile(accept = '.json'): Promise<string | null> {
  return pickFile(accept).then((f) => (f ? f.text().catch(() => null) : null));
}

/** Pick an image file and decode it, as the element the canvas will draw and the URL that
 *  element is holding — the caller lets the URL go when it lets the picture go.
 *
 *  An object URL rather than a data URL: the picture is a photograph, and reading a ten-megabyte
 *  one into a base64 string to hand straight back to the same browser is work nobody asked for. */
export async function openImage():
    Promise<{ image: HTMLImageElement; name: string; url: string } | null> {
  const f = await pickFile('image/*');
  if (!f) return null;
  const url = URL.createObjectURL(f);
  const image = new Image();
  image.src = url;
  try {
    // `decode()` and not `load`: `load` only promises the bytes and the intrinsic size, and
    // leaves the decode to the first `drawImage` — which happens inside a frame callback, so a
    // twelve-megapixel photograph would spend it stalling the canvas.  Decoded here, the frame
    // that first draws the picture costs what every later one does.
    await image.decode();
    return { image, name: f.name, url };
  } catch {
    URL.revokeObjectURL(url);              // nothing will ever draw it, so nothing will free it
    return null;
  }
}

/** Make a floating window draggable by a handle inside it, kept inside its offset parent.
 *  Where it sits is the user's, so it is written into `left`/`top` once and the CSS corner it
 *  started in is dropped — nothing re-places it afterwards. */
export function dragWindow(win: HTMLElement, handle: HTMLElement): void {
  handle.addEventListener('pointerdown', (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const box = win.getBoundingClientRect();
    const par = (win.offsetParent ?? document.body).getBoundingClientRect();
    const dx = e.clientX - box.left;
    const dy = e.clientY - box.top;
    const move = (m: PointerEvent): void => {
      const x = Math.max(0, Math.min(par.width - box.width, m.clientX - par.left - dx));
      const y = Math.max(0, Math.min(par.height - box.height, m.clientY - par.top - dy));
      win.style.left = `${x}px`;
      win.style.top = `${y}px`;
      win.style.right = 'auto';
    };
    handle.setPointerCapture(e.pointerId);
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', () => handle.removeEventListener('pointermove', move),
                            { once: true });
  });
}
